import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import { AlertTriangle } from 'lucide-react';

interface NoTasksWarningIconProps {
  /** True when the job has at least one live task (planner job.has_tasks). */
  hasTasks: boolean | undefined;
  /** True when the job has a live schedule row (planner job.is_scheduled). */
  isScheduled: boolean | undefined;
}

/**
 * Compact alert icon + tooltip for a scheduled job that has no tasks — the state
 * in which the job is invisible on every engineer's mobile device.
 *
 * The icon form exists because the planner card and the preview modal have no
 * room for the full NoTasksNotice banner; the wording matches it so all four
 * surfaces (job list badge, detail banner, planner card, preview modal) describe
 * the same fault identically (WRK-5792).
 *
 * Self-gates like its siblings: renders nothing unless the job is scheduled AND
 * confirmed task-less, so call sites stay unconditional. An unscheduled job is
 * not yet expected on mobile, and an undefined flag (older payload) is not a
 * confirmed fault.
 *
 * TooltipProvider is mounted app-wide in AppProviders, so none is needed here.
 */
export function NoTasksWarningIcon({ hasTasks, isScheduled }: NoTasksWarningIconProps) {
  if (!isScheduled) return null;
  if (hasTasks !== false) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="inline-flex flex-shrink-0 items-center"
          // The planner card is a click target that opens the preview modal;
          // the icon is informational, so keep the click on the card itself.
          onClick={(event) => event.stopPropagation()}
        >
          <AlertTriangle
            className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400"
            aria-label="Not visible on mobile: this job has no tasks"
          />
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <span>This job won't show up on mobile until it has at least one task.</span>
      </TooltipContent>
    </Tooltip>
  );
}
